import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const statusBadge = (isActive) =>
  isActive
    ? 'bg-green-100 text-green-700 border border-green-200'
    : 'bg-yellow-100 text-yellow-700 border border-yellow-200';

const CompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all'); // all | active | inactive
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null); // company detail modal
  const [actionLoading, setActionLoading] = useState('');
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (search) params.search = search;
      const res = await api.get('/companies', { params });
      setCompanies(res.data.data);
    } catch {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const approve = async (id) => {
    setActionLoading(id + '-approve');
    try {
      await api.patch(`/companies/${id}/approve`);
      fetchCompanies();
      setSelected(null);
    } catch (e) {
      alert(e.response?.data?.message || 'خطأ');
    } finally {
      setActionLoading('');
    }
  };

  const openDetail = (c) => {
    setSelected(c);
  };

  const toggleStatus = async (id, currentStatus) => {
    const label = currentStatus ? 'إيقاف' : 'تفعيل';
    if (!window.confirm(`هل تريد ${label} هذه الشركة؟`)) return;
    setActionLoading(id + '-toggle');
    try {
      await api.patch(`/companies/${id}/toggle-status`);
      fetchCompanies();
    } catch (e) {
      alert(e.response?.data?.message || 'خطأ');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">إدارة الشركات</h1>
          <p className="text-gray-500 text-sm">إجمالي: {companies.length} شركة</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="بحث باسم الشركة..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-w-48"
        />
        {['all', 'active', 'inactive'].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === f ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {f === 'all' ? 'الكل' : f === 'active' ? 'نشطة' : 'معلقة / موقوفة'}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-20 text-gray-400">جاري التحميل...</div>
      ) : companies.length === 0 ? (
        <div className="text-center bg-white rounded-xl shadow py-20 text-gray-400">
          <div className="text-5xl mb-3">🏢</div>
          <p>لا توجد شركات</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-right px-4 py-3 text-gray-600 font-semibold">الشركة</th>
                <th className="text-right px-4 py-3 text-gray-600 font-semibold">المدير</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">الحافلات</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">الرحلات</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">الحجوزات</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">الحالة</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {companies.map((c) => {
                const admin = c.staff?.[0];
                const busy  = actionLoading.startsWith(c.id);
                return (
                  <tr key={c.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-800">{c.name}</p>
                      <p className="text-gray-400 text-xs">{c.email}</p>
                      <p className="text-gray-400 text-xs">{c.phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      {admin ? (
                        <>
                          <p className="text-gray-700">{admin.name}</p>
                          <p className="text-gray-400 text-xs">{admin.email}</p>
                        </>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-gray-700">{c.busCount}</td>
                    <td className="px-4 py-3 text-center font-bold text-gray-700">{c.tripCount}</td>
                    <td className="px-4 py-3 text-center font-bold text-gray-700">{c.bookingCount}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusBadge(c.isActive)}`}>
                        {c.isActive ? 'نشطة' : 'معلقة'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <button onClick={() => openDetail(c)}
                          className="text-blue-600 hover:underline text-xs">تفاصيل</button>
                        {!c.isActive && (
                          <button onClick={() => approve(c.id)} disabled={busy}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded-lg disabled:opacity-50">
                            قبول
                          </button>
                        )}
                        <button onClick={() => toggleStatus(c.id, c.isActive)} disabled={busy}
                          className={`text-xs px-3 py-1 rounded-lg disabled:opacity-50 ${c.isActive ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}>
                          {c.isActive ? 'إيقاف' : 'تفعيل'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">{selected.name}</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-gray-400">البريد الإلكتروني</p><p className="font-medium">{selected.email}</p></div>
                <div><p className="text-gray-400">الهاتف</p><p className="font-medium">{selected.phone || '—'}</p></div>
                <div className="col-span-2"><p className="text-gray-400">العنوان</p><p className="font-medium">{selected.address || '—'}</p></div>
              </div>

              <div>
                <p className="text-gray-500 font-semibold mb-2">فريق العمل</p>
                {(selected.staff || []).map((u) => (
                  <div key={u.id} className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2 mb-1">
                    <div>
                      <p className="font-medium text-gray-800">{u.name}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${u.role === 'company_admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                      {u.role === 'company_admin' ? 'مدير' : 'سائق'}
                    </span>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-gray-500 font-semibold mb-2">الحسابات البنكية</p>
                {(selected.bankAccounts || []).length === 0
                  ? <p className="text-gray-400 text-sm">لم تُضف حسابات بنكية بعد</p>
                  : (selected.bankAccounts || []).map((b, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 mb-1 text-sm">
                      <p className="font-medium">{b.bankName}</p>
                      <p className="text-gray-400">{b.accountNumber} — {b.accountHolder}</p>
                    </div>
                  ))}
              </div>

              {selected.commissionProfile && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
                  <p className="text-xs text-emerald-600 font-medium mb-0.5">ملف تعريف العمولة المعين</p>
                  <p className="font-bold text-emerald-700">{selected.commissionProfile.name}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {!selected.isActive && (
                  <button onClick={() => approve(selected.id)} disabled={!!actionLoading}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold disabled:opacity-50">
                    قبول الشركة وتفعيلها
                  </button>
                )}
                <button onClick={() => toggleStatus(selected.id, selected.isActive)} disabled={!!actionLoading}
                  className={`flex-1 py-2 rounded-lg font-semibold disabled:opacity-50 ${selected.isActive ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                  {selected.isActive ? 'إيقاف الشركة' : 'تفعيل الشركة'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesPage;
